import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Snackbar,
  Button,
} from "@mui/material";
import {
  FileText,
  Link as LinkIcon,
  RefreshCw,
  PlusCircle,
  Copy,
  ExternalLink,
  Check,
} from "lucide-react";

import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import { toast } from "react-toastify";

const CreateForm = () => {
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/event/get-reg-form-links`
  );
  // Tracks which link was just copied (e.g., 'template1-preview')
  const [copiedKey, setCopiedKey] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // Tracks which specific template is currently being created/regenerated
  const [creatingTemplate, setCreatingTemplate] = useState(null);

  const handleCreateLink = useCallback(
    async (template) => {
      try {
        setCreatingTemplate(template);
        const response = await axios.post(
          `${API_ENDPOINT}/event/create-reg-form-link`,
          { template },
          getHeader()
        );
        toast.success(response.data.message);
        refetch();
      } catch (error) {
        toastError(error);
      } finally {
        setCreatingTemplate(null);
      }
    },
    [refetch]
  );

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Container>
        <Box sx={{ mb: 3 }}>
          <BackBtn />
        </Box>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Container>
    );
  }

  // Safely extract the links array from the response data
  const registrationForms = data?.registrationLinks || [];

  const handleCopy = async (link, uniqueKey) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedKey(uniqueKey);
      setSnackbarOpen(true);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleOpen = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <Container>
      <Box sx={{ mb: 3 }}>
        <BackBtn />
      </Box>

      {/* Page title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h1" fontWeight="700">
          Registration Form Builder Access Links
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          Generate, share, and manage registration links for each event form.
        </Typography>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Note the following details:</strong>
          <ul style={{ margin: 0, paddingLeft: "20px", marginTop: "8px" }}>
            <li>
              <strong>Link Expiration:</strong> For security purposes, generated
              builder links automatically expire after <strong>24 hours</strong>
              . Click <strong>Regenerate Link</strong> if you need a new one.
            </li>
            <li>
              <strong>Primary Identifier:</strong> Your Email ID serves as the
              primary ID for the form builder. You can specify this and
              configure additional settings within the builder interface.
            </li>
            <li>
              <strong>Phone Verification:</strong> If enabled in your settings,
              phone number verification will be required for users during the
              registration process.
            </li>
          </ul>
        </Alert>
      </Box>

      {registrationForms.length === 0 ? (
        <Alert severity="info">
          No registration forms found for this event.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {registrationForms.map((formItem, index) => {
            const key = formItem.template || index;

            return (
              <Grid item xs={12} md={6} key={key}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Card Header / Name */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      mb={2}
                    >
                      <FileText size={24} color="#1976d2" />
                      <Typography variant="h6" component="h2" fontWeight="600">
                        {formItem.name}
                      </Typography>
                    </Stack>

                    {/* Conditional Rendering based on link existence */}
                    <Box sx={{ mt: "auto", pt: 2 }}>
                      {formItem.link ? (
                        <Box>
                          {/* EDIT / UPDATE LINK - Hidden text & copy, just an Open button */}
                          <Typography
                            variant="subtitle2"
                            color="text.primary"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            Edit / Update Link
                          </Typography>

                          <Button
                            variant="contained"
                            fullWidth
                            disableElevation
                            startIcon={<ExternalLink size={18} />}
                            onClick={() => handleOpen(formItem.link)}
                            sx={{ mb: 3 }}
                          >
                            Open Builder Link
                          </Button>

                          {/* LIVE PREVIEW LINK */}
                          {formItem.preview && (
                            <>
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                                sx={{ mb: 0.5, fontWeight: 600 }}
                              >
                                Live Preview Link
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                  mb: 3,
                                  p: 1.5,
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                }}
                              >
                                <LinkIcon size={18} color="#666" />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    wordBreak: "break-all",
                                    flexGrow: 1,
                                  }}
                                >
                                  {formItem.preview}
                                </Typography>

                                {/* Copy preview link */}
                                <Tooltip
                                  title={
                                    copiedKey === `${key}-preview`
                                      ? "Copied!"
                                      : "Copy preview link"
                                  }
                                  arrow
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleCopy(
                                        formItem.preview,
                                        `${key}-preview`
                                      )
                                    }
                                    aria-label="Copy preview link"
                                  >
                                    {copiedKey === `${key}-preview` ? (
                                      <Check size={16} color="#2e7d32" />
                                    ) : (
                                      <Copy size={16} />
                                    )}
                                  </IconButton>
                                </Tooltip>

                                {/* Open preview link */}
                                <Tooltip
                                  title="Open preview link in new tab"
                                  arrow
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpen(formItem.preview)}
                                    aria-label="Open preview link"
                                  >
                                    <ExternalLink size={16} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </>
                          )}

                          {/* REGENERATE LINK BUTTON */}
                          <LoadingButton
                            variant="outlined"
                            startIcon={<RefreshCw size={18} />}
                            fullWidth
                            loadingPosition="start"
                            loading={creatingTemplate === formItem.template}
                            disabled={
                              creatingTemplate !== null &&
                              creatingTemplate !== formItem.template
                            }
                            onClick={() => {
                              handleCreateLink(formItem.template);
                            }}
                          >
                            Regenerate Link
                          </LoadingButton>
                        </Box>
                      ) : (
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            No registration link has been generated for this
                            form yet.
                          </Typography>

                          {/* CREATE LINK BUTTON */}
                          <LoadingButton
                            variant="contained"
                            startIcon={<PlusCircle size={18} />}
                            fullWidth
                            disableElevation
                            loadingPosition="start"
                            loading={creatingTemplate === formItem.template}
                            disabled={
                              creatingTemplate !== null &&
                              creatingTemplate !== formItem.template
                            }
                            onClick={() => {
                              handleCreateLink(formItem.template);
                            }}
                          >
                            Create Link
                          </LoadingButton>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default CreateForm;
